import React, { useState } from 'react';
import { Bell, ChevronDown, Filter, Search } from 'lucide-react';
import styled from '../../styled';
import { COLORS } from '../../themes/color';

const AppFrame = styled('div')`
  width: 100%;
  overflow: hidden;
  border: 1px solid #dfe3e7;
  border-radius: 12px;
  background: #f4f4f4;
  box-shadow: 0 18px 55px rgba(14, 28, 43, 0.1);
`;

const AppHeader = styled('div')`
  display: flex;
  align-items: center;
  min-height: 42px;
  padding: 0 12px;
  border-bottom: 1px solid #dfe3e7;
  background: #fff;
  gap: 10px;
  min-width: 0;
`;

const AppLogo = styled('div')`
  flex-shrink: 0;
  padding-right: 10px;
  border-right: 1px solid #dfe3e7;
  color: #121212;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: -0.04em;
`;

const AppNav = styled('div')`
  display: flex;
  align-items: stretch;
  align-self: stretch;
  flex: 1;
  min-width: 0;
`;

const NavButton = styled('button')`
  position: relative;
  border: 0;
  padding: 0;
  background: transparent;
  color: #171717;
  font-size: 9px;
  font-weight: 700;
  cursor: default;
  white-space: nowrap;

  &::after {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 2px;
    background: ${COLORS.PRIMARY};
    content: '';
  }
`;

const UtilityNav = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1c2733;
  flex-shrink: 0;
`;

const Avatar = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #94a3b8;
  color: #fff;
  font-size: 7px;
  font-weight: 800;
`;

const AppCanvas = styled('div')`
  padding: 12px 14px 14px;
  background: #f4f4f4;
  min-width: 0;
`;

const PerformanceToolbar = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  margin-bottom: 10px;
  color: #7d8389;
  font-size: 8px;
  flex-wrap: wrap;
  min-width: 0;

  strong {
    margin-right: 2px;
    color: #24282c;
    font-size: 13px;
    white-space: nowrap;
  }
`;

const TinySearch = styled('div')`
  display: flex;
  align-items: center;
  gap: 5px;
  flex: 1;
  min-width: 0;
  max-width: 140px;
  padding: 6px 8px;
  border: 1px solid #e1e4e8;
  border-radius: 5px;
  background: #fff;
  color: #a0a5ab;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ToolbarChip = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 5px 7px;
  border-radius: 5px;
  background: #fff;
  color: #5d6268;
  line-height: 1;
  white-space: nowrap;
  flex-shrink: 0;

  svg {
    flex-shrink: 0;
  }

  @media (max-width: 480px) {
    &:nth-of-type(2) {
      display: none;
    }
  }
`;

const DateControl = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  padding: 6px 8px;
  border-radius: 5px;
  background: #fff;
  color: #5d6268;
  font-size: 8px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ReportGrid = styled('div')`
  display: grid;
  grid-template-columns: 34% minmax(0, 1fr);
  gap: 10px;
  margin-bottom: 12px;
  min-width: 0;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled('div')`
  padding: 14px 12px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #eceef1;
  min-width: 0;
`;

const CellTitle = styled('div')`
  margin-bottom: 10px;
  color: #2d3338;
  font-size: 9px;
  font-weight: 700;
`;

const EarningsTotal = styled('div')`
  margin: 6px 0 12px;
  color: #24282c;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.1;
  text-align: center;
  letter-spacing: -0.02em;
`;

const StatusRows = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StatusRow = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const StatusMeta = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 8.5px;

  .label {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #5d6268;
    font-weight: 600;
  }

  .value {
    color: #24282c;
    font-weight: 700;
    white-space: nowrap;
  }
`;

const StatusDot = styled('span')<{ $color: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  flex-shrink: 0;
`;

const StatusTrack = styled('div')`
  height: 5px;
  overflow: hidden;
  border-radius: 3px;
  background: #eef1f4;
`;

const StatusFill = styled('div')<{ $width: number; $color: string }>`
  width: ${(p) => p.$width}%;
  height: 100%;
  border-radius: 3px;
  background: ${(p) => p.$color};
`;

const ChartArea = styled('div')`
  position: relative;
  height: 190px;
  padding-top: 6px;
  min-width: 0;
`;

const ChartBars = styled('div')`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 100%;
  min-width: 0;
`;

const StackedBarButton = styled('button')<{
  $height: number;
  $active: boolean;
}>`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column-reverse;
  height: ${(p) => p.$height}%;
  min-height: 20px;
  min-width: 0;
  border: 0;
  padding: 0;
  background: transparent;
  border-radius: 3px 3px 0 0;
  overflow: hidden;
  cursor: pointer;
  box-shadow: ${(p) =>
    p.$active ? `inset 0 0 0 1px ${COLORS.PRIMARY}` : 'none'};

  &:focus-visible {
    outline: 2px solid ${COLORS.PRIMARY};
    outline-offset: 2px;
  }
`;

const StackSegment = styled('span')<{ $color: string; $flex: number }>`
  flex: ${(p) => p.$flex};
  width: 100%;
  min-height: 3px;
  background: ${(p) => p.$color};
`;

const ChartTooltip = styled('div')<{ $left: number }>`
  position: absolute;
  top: 0;
  left: ${(p) => p.$left}%;
  z-index: 2;
  min-width: 132px;
  padding: 9px 10px;
  border-radius: 8px;
  background: #1a2332;
  color: #f8fafc;
  font-size: 8.5px;
  line-height: 1.45;
  box-shadow: 0 10px 28px rgba(8, 15, 28, 0.35);
  transform: translate(-50%, 0);
  pointer-events: none;

  &::after {
    position: absolute;
    bottom: -5px;
    left: 50%;
    width: 10px;
    height: 10px;
    background: #1a2332;
    transform: translateX(-50%) rotate(45deg);
    content: '';
  }
`;

const TooltipTitle = styled('div')`
  margin-bottom: 7px;
  color: #94a3b8;
  font-size: 7.5px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const TooltipRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const TooltipLabel = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #e2e8f0;
`;

const TooltipSwatch = styled('span')<{ $color: string }>`
  width: 7px;
  height: 7px;
  border-radius: 2px;
  background: ${(p) => p.$color};
  flex-shrink: 0;
`;

const TooltipTotal = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 7px;
  padding-top: 7px;
  border-top: 1px solid rgba(148, 163, 184, 0.25);
  color: #fff;
  font-weight: 700;
`;

const ChartHeading = styled('div')`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  color: #34393f;
  font-size: 8px;
  font-weight: 700;
  min-width: 0;

  span:last-child {
    color: #7d8389;
    font-weight: 600;
    white-space: nowrap;
    flex-shrink: 0;
  }
`;

const SkeletonLine = styled('div')<{ $width?: string; $height?: string }>`
  border-radius: 4px;
  background: #e8ebee;
  width: ${(p) => p.$width || '100%'};
  height: ${(p) => p.$height || '9px'};
`;

const DateRows = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const DateRow = styled('div')`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 52px 52px;
  gap: 8px;
  align-items: center;
  min-width: 0;
`;

const STATUS_COLORS = {
  pending: COLORS.GOLD.gold4,
  paidOut: COLORS.GREEN.green9
} as const;

const STATUS_LABELS = {
  pending: 'Pending',
  paidOut: 'Paid out'
} as const;

type DayEarnings = {
  label: string;
  pending: number;
  paidOut: number;
};

const CHART_DAYS: DayEarnings[] = [
  { label: 'Apr 19', pending: 38.4, paidOut: 53.8 },
  { label: 'Apr 20', pending: 42.1, paidOut: 47.4 },
  { label: 'Apr 21', pending: 28.6, paidOut: 32.2 },
  { label: 'Apr 22', pending: 61.3, paidOut: 66.9 },
  { label: 'Apr 23', pending: 54.8, paidOut: 56.5 },
  { label: 'Apr 24', pending: 72.7, paidOut: 73.1 },
  { label: 'Apr 25', pending: 33.5, paidOut: 47.5 },
  { label: 'Apr 26', pending: 48.2, paidOut: 66.4 },
  { label: 'Apr 27', pending: 41.6, paidOut: 54.3 },
  { label: 'Apr 28', pending: 58.9, paidOut: 75.2 },
  { label: 'Apr 29', pending: 26.8, paidOut: 41.6 },
  { label: 'Apr 30', pending: 49.4, paidOut: 71.5 }
];

const formatUsd = (amount: number) =>
  `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const dayTotal = (day: DayEarnings) => day.pending + day.paidOut;

const maxDayTotal = Math.max(...CHART_DAYS.map(dayTotal));

const EarningsChart = () => {
  const [activeIndex, setActiveIndex] = useState(5);
  const activeDay = CHART_DAYS[activeIndex];
  const tooltipLeft = ((activeIndex + 0.5) / CHART_DAYS.length) * 100;

  return (
    <ChartArea>
      <ChartTooltip $left={tooltipLeft} aria-hidden>
        <TooltipTitle>{activeDay.label}</TooltipTitle>
        {(Object.keys(STATUS_LABELS) as Array<keyof typeof STATUS_LABELS>).map(
          (key) => (
            <TooltipRow key={key}>
              <TooltipLabel>
                <TooltipSwatch $color={STATUS_COLORS[key]} />
                {STATUS_LABELS[key]}
              </TooltipLabel>
              <span>{formatUsd(activeDay[key])}</span>
            </TooltipRow>
          )
        )}
        <TooltipTotal>
          <span>Daily total</span>
          <span>{formatUsd(dayTotal(activeDay))}</span>
        </TooltipTotal>
      </ChartTooltip>
      <ChartBars>
        {CHART_DAYS.map((day, index) => {
          const total = dayTotal(day);
          const height = Math.round((total / maxDayTotal) * 100);

          return (
            <StackedBarButton
              key={day.label}
              type="button"
              $height={height}
              $active={index === activeIndex}
              aria-label={`${day.label}: ${formatUsd(total)}`}
              aria-pressed={index === activeIndex}
              onMouseEnter={() => setActiveIndex(index)}
              onFocus={() => setActiveIndex(index)}
            >
              <StackSegment
                $color={STATUS_COLORS.paidOut}
                $flex={day.paidOut}
              />
              <StackSegment
                $color={STATUS_COLORS.pending}
                $flex={day.pending}
              />
            </StackedBarButton>
          );
        })}
      </ChartBars>
    </ChartArea>
  );
};

export const PaymentStatusMockup = () => (
  <AppFrame aria-hidden>
    <AppHeader>
      <AppLogo>affilimate</AppLogo>
      <AppNav aria-label="Affilimate product navigation">
        <NavButton type="button">Performance</NavButton>
      </AppNav>
      <UtilityNav>
        <Bell size={12} />
        <Avatar>AM</Avatar>
      </UtilityNav>
    </AppHeader>
    <AppCanvas>
      <PerformanceToolbar>
        <strong>Transactions</strong>
        <TinySearch>
          <Search size={10} />
          <span>Search</span>
        </TinySearch>
        <ToolbarChip>
          <Filter size={9} /> Filters
        </ToolbarChip>
        <ToolbarChip>
          Sort <ChevronDown size={9} />
        </ToolbarChip>
        <DateControl>
          Last 90 days <ChevronDown size={10} />
        </DateControl>
      </PerformanceToolbar>
      <ReportGrid>
        <Panel>
          <CellTitle>Total earnings</CellTitle>
          <EarningsTotal>$76,394.01</EarningsTotal>
          <StatusRows>
            <StatusRow>
              <StatusMeta>
                <span className="label">
                  <StatusDot $color={STATUS_COLORS.pending} />
                  Pending
                </span>
                <span className="value">$41,218.64</span>
              </StatusMeta>
              <StatusTrack>
                <StatusFill $width={54} $color={STATUS_COLORS.pending} />
              </StatusTrack>
            </StatusRow>
            <StatusRow>
              <StatusMeta>
                <span className="label">
                  <StatusDot $color={STATUS_COLORS.paidOut} />
                  Paid out
                </span>
                <span className="value">$35,175.37</span>
              </StatusMeta>
              <StatusTrack>
                <StatusFill $width={46} $color={STATUS_COLORS.paidOut} />
              </StatusTrack>
            </StatusRow>
          </StatusRows>
        </Panel>
        <Panel>
          <ChartHeading>
            <span>Daily earnings</span>
            <span>By payout status</span>
          </ChartHeading>
          <EarningsChart />
        </Panel>
      </ReportGrid>
      <DateRows>
        {[0, 1, 2, 3].map((index) => (
          <DateRow key={index}>
            <SkeletonLine $width={`${88 - index * 8}%`} $height="8px" />
            <SkeletonLine $width="100%" $height="8px" />
            <SkeletonLine $width="100%" $height="8px" />
          </DateRow>
        ))}
      </DateRows>
    </AppCanvas>
  </AppFrame>
);
